// P3-013: Turn manager — complete turn flow (GDD section 4.5.7)
//
// Turn flow:
//   1. startTurn: turn++, CD-1, enemy events, overload check
//   2. executePlayerAction: BoardSystem.trySwap -> resolveMatches
//   3. resolveTurn: calculate damage + trigger skills for each cascade step
//   4. endTurn: buff tick, remove expired, check win/loss

import type { TilePosition, CascadeResult } from '../types/board';
import type { BattleTurnResult, MatchResolution, OverloadCheckResult } from '../types/battle-internal';
import { useBattleStore } from '../stores/battleStore';
import * as BoardSystem from './BoardSystem';
import { calculateStepDamage } from './DamageCalculator';
import { processSkillTriggersForMatches } from './SkillExecutor';
import { checkHPThresholds, applyHPThresholdEvent, applyEnemyTypeMechanic, checkOverload, checkDemandChangeExpiry } from './EnemyBehavior';

export interface TurnStartResult {
  turnNumber: number;
  overloadEvent: OverloadCheckResult | null;
  enemyTypeEvent: { deadlineReduced: boolean; legacyActive: boolean };
  hpThresholdTriggered: boolean;
}

/**
 * Phase 1: Start of turn.
 * - Increment turn counter
 * - Decrement all skill CDs
 * - Check enemy type mechanics
 * - Check HP thresholds
 * - Check overload
 * - Enable all agent skills (reset from last turn's overload)
 */
export function startTurn(): TurnStartResult {
  const battleStore = useBattleStore.getState();

  // Increment turn
  battleStore.incrementTurn();
  const turnNumber = useBattleStore.getState().currentTurn;

  // Enable all agent skills (reset from previous turn overload)
  battleStore.enableAllAgentSkills();

  // Decrement all skill CDs
  battleStore.decrementAllCDs();

  // Reset combo counter
  battleStore.setCombo(0);

  // Check enemy type mechanics (IMPATIENT/LEGACY)
  const enemy = useBattleStore.getState().enemy;
  let enemyTypeEvent = { deadlineReduced: false, legacyActive: false };
  if (enemy) {
    enemyTypeEvent = applyEnemyTypeMechanic(enemy.type, turnNumber);
  }

  // Check HP threshold events
  let hpThresholdTriggered = false;
  if (enemy) {
    const thresholdCheck = checkHPThresholds(enemy);
    if (thresholdCheck.triggered && thresholdCheck.event) {
      applyHPThresholdEvent(thresholdCheck.event, thresholdCheck.tilesToLock);
      hpThresholdTriggered = true;
    }
  }

  // Check overload
  const overloadLevel = useBattleStore.getState().overloadLevel;
  const overloadEvent = checkOverload(overloadLevel);

  return {
    turnNumber,
    overloadEvent: overloadEvent.triggered ? overloadEvent : null,
    enemyTypeEvent,
    hpThresholdTriggered,
  };
}

/**
 * Phase 2: Execute player action (swap tiles).
 * Returns the cascade result or null if the swap is invalid.
 */
export function executePlayerAction(
  pos1: TilePosition,
  pos2: TilePosition,
): CascadeResult | null {
  return BoardSystem.performSwapAndResolve(pos1, pos2);
}

/**
 * Phase 3: Resolve turn — calculate damage and trigger skills for each cascade step.
 */
export function resolveTurn(cascadeResult: CascadeResult): {
  matchResolutions: MatchResolution[];
  totalDamage: number;
  totalSkillsTriggered: number;
  totalCrits: number;
} {
  const battleStore = useBattleStore.getState();
  const allResolutions: MatchResolution[] = [];
  let totalDamage = 0;
  let totalSkillsTriggered = 0;
  let totalCrits = 0;

  for (let stepIdx = 0; stepIdx < cascadeResult.steps.length; stepIdx++) {
    const step = cascadeResult.steps[stepIdx]!;
    const comboCount = stepIdx + 1;

    // Update combo
    battleStore.setCombo(comboCount);

    // Get current state for calculations
    const currentState = useBattleStore.getState();
    const enemy = currentState.enemy;
    if (!enemy) break;

    // Calculate damage for all matches in this step
    // Pass total tiles removed this round for combo weight tier calculation
    const roundTileCount = step.removedPositions.length;
    const { damages, totalDamage: stepDamage, critCount } = calculateStepDamage(
      step.matches,
      currentState.team,
      comboCount,
      currentState.buffs,
      enemy.phase,
      roundTileCount,
    );

    // Apply damage to enemy
    battleStore.dealDamage(stepDamage);
    totalDamage += stepDamage;
    totalCrits += critCount;

    if (critCount > 0) {
      battleStore.incrementStat('critCount', critCount);
    }

    // Process skill triggers for this step's match elements
    const matchElements = step.matches.map((m) => m.element);
    const skillTriggers = processSkillTriggersForMatches(matchElements);
    totalSkillsTriggered += skillTriggers.length;

    // Build match resolutions
    for (let i = 0; i < step.matches.length; i++) {
      const match = step.matches[i]!;
      const damage = damages[i]!;
      const trigger = skillTriggers.find(
        (t) => {
          const agent = currentState.team.find((a) => a.agentId === t.agentId);
          return agent?.elementAffinity === match.element;
        }
      );

      allResolutions.push({
        match,
        damage,
        skillTriggered: trigger
          ? { agentId: trigger.agentId, skill: trigger.skill }
          : undefined,
      });
    }

    // Check HP thresholds after each step
    const updatedEnemy = useBattleStore.getState().enemy;
    if (updatedEnemy) {
      const thresholdCheck = checkHPThresholds(updatedEnemy);
      if (thresholdCheck.triggered && thresholdCheck.event) {
        applyHPThresholdEvent(thresholdCheck.event, thresholdCheck.tilesToLock);
      }
    }
  }

  return {
    matchResolutions: allResolutions,
    totalDamage,
    totalSkillsTriggered,
    totalCrits,
  };
}

/**
 * Phase 4: End of turn.
 * - Tick buffs (duration-1, remove expired)
 * - Check win/loss conditions
 */
export function endTurn(): 'VICTORY' | 'DEFEAT' | null {
  const battleStore = useBattleStore.getState();

  // Tick buffs
  battleStore.tickBuffs();

  // Check demand-change phase expiry
  checkDemandChangeExpiry();

  const enemy = useBattleStore.getState().enemy;

  // Check victory: enemy HP <= 0
  if (enemy && enemy.hp <= 0) {
    battleStore.setResult('VICTORY');
    return 'VICTORY';
  }

  // Check defeat: current turn > deadline
  const { currentTurn, maxTurns } = useBattleStore.getState();
  if (currentTurn >= maxTurns) {
    battleStore.setResult('DEFEAT');
    return 'DEFEAT';
  }

  return null;
}

/**
 * Execute a complete turn: validate swap -> startTurn -> executePlayerAction -> resolveTurn -> endTurn.
 * This is the main entry point from the orchestrator.
 *
 * Validates the swap BEFORE committing any state changes (startTurn),
 * preventing state corruption on invalid swaps.
 */
export function executeTurn(
  pos1: TilePosition,
  pos2: TilePosition,
): BattleTurnResult | null {
  // Pre-validate: check if the swap would be valid BEFORE modifying any state
  if (!BoardSystem.wouldBeValidSwap(pos1, pos2)) {
    return null;
  }

  // Phase 1: Start turn (safe — swap is guaranteed to be valid)
  const turnStart = startTurn();

  // Phase 2: Player action
  const cascadeResult = executePlayerAction(pos1, pos2);
  if (!cascadeResult) {
    // Should not happen after pre-validation, but handle defensively
    return null;
  }

  // Phase 3: Resolve damage and skills
  const resolution = resolveTurn(cascadeResult);

  // Phase 4: End turn
  const outcome = endTurn();

  const enemy = useBattleStore.getState().enemy;

  return {
    turnNumber: turnStart.turnNumber,
    cascadeSteps: cascadeResult.steps,
    matchResolutions: resolution.matchResolutions,
    totalDamage: resolution.totalDamage,
    skillsTriggered: resolution.totalSkillsTriggered,
    cascadeCount: cascadeResult.steps.length,
    enemyHpAfter: enemy?.hp ?? 0,
    overloadEvent: turnStart.overloadEvent ?? undefined,
    battleOutcome: outcome,
  };
}
